/**
 * HeroLatestArticle — full-width overlay for the profile page banner.
 *
 * Fetches the writer's most recent published article and renders a
 * beautiful glass-morphism card ON TOP of the cover so the banner
 * area actually promotes what the writer is writing about.
 *
 * Empty states:
 *   • Owner with 0 articles  → "Start writing" CTA
 *   • Visitor with 0 articles → renders nothing (banner stays clean)
 *
 * Backend: GET /api/publisher/v1/authors/{author_id}/articles?limit=1
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, PenLine, ArrowRight, BookOpen, Clock } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function useLatestArticle(authorId) {
  const [state, setState] = useState({ loading: true, article: null });
  useEffect(() => {
    if (!authorId) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/authors/${authorId}/articles?limit=1`);
        if (!r.ok) throw new Error();
        const d = await r.json();
        const a = (d.articles || []).find((x) => x.status === "published") || null;
        if (alive) setState({ loading: false, article: a });
      } catch {
        if (alive) setState({ loading: false, article: null });
      }
    })();
    return () => { alive = false; };
  }, [authorId]);
  return state;
}

export default function HeroLatestArticle({ authorId, handle, isOwner, displayName }) {
  const { loading, article } = useLatestArticle(authorId);

  if (loading) return null;

  // Owner + no articles yet → prominent "Start writing" CTA
  if (!article && isOwner) {
    return (
      <div
        className="absolute inset-x-0 bottom-0 top-0 flex items-center justify-center p-6"
        data-testid="hero-latest-empty-owner"
      >
        <div className="max-w-md text-center bg-white/95 backdrop-blur-md rounded-2xl px-6 py-5 shadow-lg border border-white/50">
          <PenLine className="w-6 h-6 mx-auto mb-2 text-blue-700" />
          <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
            Your banner is your billboard
          </p>
          <h3 className="text-base font-bold text-slate-900 mt-1">
            Write your first article — this space showcases your latest.
          </h3>
          <Link
            to="/publisher/write"
            className="mt-3 inline-flex items-center gap-1.5 bg-slate-900 text-white px-4 py-2 rounded-full text-xs font-semibold hover:bg-slate-700"
            data-testid="hero-latest-write-cta"
          >
            <PenLine className="w-3.5 h-3.5" /> Start writing
          </Link>
        </div>
      </div>
    );
  }

  if (!article) return null;

  const cover = article.cover_image_url;
  const published = article.published_at
    ? new Date(article.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <div
      className="absolute inset-x-4 sm:inset-x-8 top-4 sm:top-6 max-w-[560px]"
      data-testid="hero-latest-article"
    >
      <Link
        to={`/blog/${article.slug}`}
        className="group flex items-stretch gap-3 bg-white/92 backdrop-blur-md rounded-2xl overflow-hidden shadow-lg border border-white/70 hover:shadow-xl transition-shadow"
      >
        {cover ? (
          <img
            src={cover}
            alt=""
            className="w-24 sm:w-32 h-full min-h-[112px] object-cover shrink-0 bg-slate-100"
            loading="lazy"
          />
        ) : (
          <div className="w-24 sm:w-32 min-h-[112px] shrink-0 bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center">
            <BookOpen className="w-8 h-8 text-white/80" />
          </div>
        )}
        <div className="flex-1 min-w-0 py-3 pr-3 sm:pr-4 flex flex-col justify-center">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-teal-700 font-bold mb-1">
            <Sparkles className="w-3 h-3" />
            Latest on SocialPulse.blog
          </div>
          <h3
            className="text-sm sm:text-base font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-blue-700"
            data-testid="hero-latest-article-title"
          >
            {article.title}
          </h3>
          <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
            {handle && <span className="font-semibold text-slate-700">@{handle}</span>}
            {published && <><span>·</span><span>{published}</span></>}
            {article.views_total > 0 && (
              <><span>·</span><span>{article.views_total.toLocaleString()} views</span></>
            )}
          </div>
          <span className="mt-2 inline-flex items-center gap-0.5 text-xs font-bold text-blue-700 group-hover:text-blue-900">
            Read now <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </Link>
    </div>
  );
}
